import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import {
  AlertCircle,
  CheckCircle2,
  CloudUpload,
  Download,
  FileText,
  Loader2,
  Package,
  Target,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { YearFilter } from "@/components/filters/YearFilter";
import { useToast } from "@/hooks/use-toast";
import { apiFetch } from "@/lib/http";
import { useHydrateYearFromStorage, useYear } from "@/state/yearStore";
import { bestFuzzyMatchByName } from "@shared/nameSimilarity";
import { adviceForImportAnalyseError } from "@shared/campaignImportHints";
import { buildImportedPackGroups, packNamesForRow } from "@shared/packImportGroups";

type WizardStep = 1 | 2 | 3 | 4;

type RetailerOption = { id: number; name: string };
type CycleOption = {
  id: number;
  name: string;
  status?: string;
  startDate?: string | null;
  endDate?: string | null;
};
type PackOption = { id: number; name: string };

type DetectedPack = {
  importPackName: string;
  normalizedName: string;
  matchedPackId: number | null;
  matchedPackName: string | null;
  suggestedPackId: number | null;
  suggestedPackName: string | null;
  suggestedScore: number | null;
  status: "matched" | "suggested" | "unmatched" | "ambiguous";
  ambiguousMatches: Array<{ id: number; name: string }>;
  isLocked: boolean | null;
  rowCount: number;
};

type PackGroupSeed = {
  packId: number;
  packName: string;
  storeIds: number[];
  formatEntries: Array<{ mediaFormatId: number; quantity: number }>;
};

type CampaignTile = {
  campaignName: string;
  importPackName: string;
  importPackNames?: string[];
  storeGroup: number | null;
  comments: string | null;
  rowNumber: number;
  packStatus: DetectedPack["status"] | "missing_pack_name";
};

type ImportRow = {
  rowNumber: number;
  campaignName: string;
  importPackName: string;
  importPackNames?: string[];
  storeGroup: number | null;
  comments: string | null;
};

type SkippedRow = {
  rowNumber: number;
  reason: "missing_campaign" | "missing_pack";
  campaignName: string;
  importPackName: string;
};

type AnalysisResponse = {
  sheetName: string | null;
  campaigns: CampaignTile[];
  rows: ImportRow[];
  skippedRows: SkippedRow[];
  detectedPacks: DetectedPack[];
  groupSeeds?: PackGroupSeed[];
  validation: { blocking: string[]; warnings: string[] };
  counts: { campaignsDetected: number; uniquePackNames: number; rowsParsed: number; rowsSkipped: number };
};

type PackDecision = {
  action: "matched" | "use_existing" | "unresolved";
  packId: number | null;
  isSuggested?: boolean;
};

const STEP_LABELS: Array<{ id: WizardStep; label: string }> = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Detected Campaigns" },
  { id: 3, label: "Pack Validation" },
  { id: 4, label: "Create" },
];

type WizardPillTone = "neutral" | "active" | "danger" | "warning" | "success" | "info";

const WIZARD_PILL_STYLES: Record<WizardPillTone, CSSProperties> = {
  neutral: { backgroundColor: "#f3f4f6", color: "#111827", borderColor: "#d1d5db" },
  active: { backgroundColor: "#1d4ed8", color: "#ffffff", borderColor: "#1e40af" },
  danger: { backgroundColor: "#b91c1c", color: "#ffffff", borderColor: "#991b1b" },
  warning: { backgroundColor: "#b45309", color: "#ffffff", borderColor: "#92400e" },
  success: { backgroundColor: "#166534", color: "#ffffff", borderColor: "#14532d" },
  info: { backgroundColor: "#0f766e", color: "#ffffff", borderColor: "#115e59" },
};

function WizardPill({ children, tone = "neutral" }: { children: ReactNode; tone?: WizardPillTone }) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium"
      style={WIZARD_PILL_STYLES[tone]}
    >
      {children}
    </span>
  );
}

function formatCycleDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-IE", { day: "2-digit", month: "short", year: "numeric" });
}

function packDecisionKey(rowNumber: number, packName: string): string {
  return `${rowNumber}::${packName}`;
}

function getCycleOptionLabel(cycle: CycleOption): string {
  const parts = [cycle.name];
  const start = formatCycleDate(cycle.startDate);
  const end = formatCycleDate(cycle.endDate);
  if (start && end) parts.push(`${start} - ${end}`);
  else if (start ?? end) parts.push(start ?? end!);
  if (cycle.status) parts.push(cycle.status);
  return parts.join(" - ");
}

export default function CampaignUploadPacks() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const importInputRef = useRef<HTMLInputElement>(null);
  const cycleLoadRequestRef = useRef(0);
  const analysisRequestRef = useRef(0);
  useHydrateYearFromStorage();
  const selectedYear = useYear();

  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [retailers, setRetailers] = useState<RetailerOption[]>([]);
  const [cycles, setCycles] = useState<CycleOption[]>([]);
  const [packOptions, setPackOptions] = useState<PackOption[]>([]);
  const [selectedRetailerId, setSelectedRetailerId] = useState<number | null>(null);
  const [selectedCycleId, setSelectedCycleId] = useState<number | null>(null);
  const [packDecisions, setPackDecisions] = useState<Record<string, PackDecision>>({});
  const [packLockStatus, setPackLockStatus] = useState<Record<number, "locked" | "unlocked" | "loading">>({});
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [isCyclesLoading, setIsCyclesLoading] = useState(false);
  const [isCreatingCampaigns, setIsCreatingCampaigns] = useState(false);
  const [createdCampaigns, setCreatedCampaigns] = useState<Array<{ id: number; name: string }>>([]);
  const [campaignCreationStatus, setCampaignCreationStatus] = useState<
    Array<{ name: string; status: "pending" | "creating" | "success" | "failed"; id?: number; error?: string }>
  >([]);

  const resetImportRunState = () => {
    analysisRequestRef.current += 1;
    setAnalysis(null);
    setPackDecisions({});
    setCreatedCampaigns([]);
    setCampaignCreationStatus([]);
    setIsAnalysing(false);
    setCurrentStep(1);
  };

  const stepProgress = useMemo(() => (currentStep / STEP_LABELS.length) * 100, [currentStep]);

  const resolvePackIdForCampaign = (rowNumber: number, importPackName: string): number | null => {
    const decision = packDecisions[packDecisionKey(rowNumber, importPackName)];
    if (!decision) return null;
    if (decision.action === "unresolved") return null;
    return decision.packId;
  };

  const hasUnresolvedPackDecision = useMemo(() => {
    if (!analysis) return true;
    return analysis.campaigns.some((campaign) =>
      packNamesForRow(campaign).some((packName) => {
        const decision = packDecisions[packDecisionKey(campaign.rowNumber, packName)];
        if (!decision || decision.action === "unresolved") return true;
        return !decision.packId;
      }),
    );
  }, [analysis, packDecisions]);

  const hasSuggestedPackDecisions = useMemo(
    () => Object.values(packDecisions).some((d) => d.isSuggested),
    [packDecisions],
  );

  const hasUnlockedSelectedPacks = useMemo(() => {
    return Object.values(packDecisions).some((decision) => {
      if (!decision.packId) return false;
      return packLockStatus[decision.packId] === "unlocked";
    });
  }, [packDecisions, packLockStatus]);

  const hasPendingLockStatus = useMemo(() => {
    return Object.values(packDecisions).some((decision) => {
      if (!decision.packId) return false;
      const status = packLockStatus[decision.packId];
      return status === undefined || status === "loading";
    });
  }, [packDecisions, packLockStatus]);

  const detectedPackByName = useMemo(() => {
    const map = new Map<string, DetectedPack>();
    for (const pack of analysis?.detectedPacks ?? []) {
      map.set(pack.importPackName, pack);
    }
    return map;
  }, [analysis]);


  const canProceedFromStep = (step: WizardStep) => {
    if (step === 1) return Boolean(selectedRetailerId && selectedCycleId && importFile && analysis);
    if (step === 2) return Boolean(analysis && analysis.campaigns.length > 0);
    if (step === 3) {
      return Boolean(
        analysis &&
        !hasUnresolvedPackDecision &&
        !hasSuggestedPackDecisions &&
        !hasUnlockedSelectedPacks &&
        !hasPendingLockStatus,
      );
    }
    if (step === 4) return true;
    return false;
  };

  const getStepBlockReason = (step: WizardStep): string | null => {
    if (step === 1) {
      if (!selectedRetailerId) return "Select a retailer.";
      if (!selectedCycleId) return "Select a cycle.";
      if (!importFile) return "Upload a campaign pack booking file.";
      if (!analysis) return "Run analysis on the uploaded file.";
    }
    if (step === 3) {
      if (hasUnresolvedPackDecision) return "Confirm every pack mapping before continuing.";
      if (hasSuggestedPackDecisions) return "Confirm or change every suggested pack mapping.";
      if (hasPendingLockStatus) return "Wait for pack lock status to finish loading.";
      if (hasUnlockedSelectedPacks) return "Selected packs must be locked before creating campaigns.";
    }
    return null;
  };

  const currentStepBlockReason = getStepBlockReason(currentStep);
  const campaignsListUrl =
    selectedRetailerId && selectedCycleId
      ? `/campaigns?retailerId=${selectedRetailerId}&cycleId=${selectedCycleId}&year=${selectedYear}`
      : "/campaigns";

  const loadRetailers = async () => {
    if (retailers.length > 0) return;
    const response = await apiFetch("/api/retailers");
    if (!response.ok) return;
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
    setRetailers(
      items
        .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
        .map((item: any) => ({ id: item.id as number, name: item.name as string }))
        .sort((a: RetailerOption, b: RetailerOption) => a.name.localeCompare(b.name)),
    );
  };

  const loadCyclesForRetailer = async (retailerId: number, year: number) => {
    const requestId = cycleLoadRequestRef.current + 1;
    cycleLoadRequestRef.current = requestId;
    setIsCyclesLoading(true);
    setCycles([]);
    setSelectedCycleId(null);
    try {
      const response = await apiFetch(`/api/booking-cycles?retailerId=${retailerId}&year=${year}`);
      if (requestId !== cycleLoadRequestRef.current) return;
      if (!response.ok) return;
      const payload = await response.json();
      const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
      setCycles(
        items
          .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
          .map((item: any) => ({
            id: item.id as number,
            name: item.name as string,
            status: item.status,
            startDate: item.startDate ?? null,
            endDate: item.endDate ?? null,
          }))
          .sort((a: CycleOption, b: CycleOption) => b.id - a.id),
      );
    } finally {
      if (requestId === cycleLoadRequestRef.current) setIsCyclesLoading(false);
    }
  };

  const loadRetailerPacks = async () => {
    if (!selectedRetailerId) return;
    const response = await apiFetch(`/api/retailers/${selectedRetailerId}/packs`);
    if (!response.ok) return;
    const payload = await response.json();
    const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
    setPackOptions(
      items
        .filter((item: any) => item && typeof item.id === "number" && typeof item.name === "string")
        .map((item: any) => ({ id: item.id as number, name: item.name as string }))
        .sort((a: PackOption, b: PackOption) => a.name.localeCompare(b.name)),
    );
  };

  useEffect(() => {
    void loadRetailers();
  }, []);

  useEffect(() => {
    if (!selectedRetailerId) return;
    resetImportRunState();
    void loadCyclesForRetailer(selectedRetailerId, selectedYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRetailerId, selectedYear]);

  useEffect(() => {
    if (currentStep === 3) void loadRetailerPacks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, selectedRetailerId]);

  useEffect(() => {
    const packIds = Array.from(
      new Set(
        Object.values(packDecisions)
          .map((decision) => decision.packId)
          .filter((id): id is number => typeof id === "number" && id > 0),
      ),
    );
    if (packIds.length === 0) {
      setPackLockStatus({});
      return;
    }

    let cancelled = false;
    setPackLockStatus((prev) => {
      const next = { ...prev };
      for (const packId of packIds) next[packId] = "loading";
      return next;
    });

    void (async () => {
      const response = await apiFetch("/api/packs/status-batch", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packIds }),
      });
      if (cancelled || !response.ok) return;
      const payload = await response.json();
      const items = Array.isArray(payload) ? payload : (payload?.data ?? []);
      setPackLockStatus((prev) => {
        const next = { ...prev };
        for (const item of items) {
          if (typeof item?.packId === "number") {
            next[item.packId] = item.status === "locked" ? "locked" : "unlocked";
          }
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [packDecisions]);

  useEffect(() => {
    if (!analysis || packOptions.length === 0) return;
    setPackDecisions((prev) => {
      const next = { ...prev };
      let changed = false;
      const detectedByName = new Map((analysis.detectedPacks ?? []).map((pack) => [pack.importPackName, pack]));
      for (const campaign of analysis.campaigns) {
        for (const packName of packNamesForRow(campaign)) {
          const key = packDecisionKey(campaign.rowNumber, packName);
          if (next[key]) continue;
          const pack = detectedByName.get(packName);
          if (pack?.status === "matched" && pack.matchedPackId) {
            next[key] = { action: "matched", packId: pack.matchedPackId };
          } else if (pack?.status === "suggested" && pack.suggestedPackId) {
            next[key] = { action: "use_existing", packId: pack.suggestedPackId, isSuggested: true };
          } else if (pack?.status === "unmatched") {
            const clientSuggestion = bestFuzzyMatchByName(pack.importPackName, packOptions);
            next[key] = clientSuggestion
              ? { action: "use_existing", packId: clientSuggestion.id, isSuggested: true }
              : { action: "unresolved", packId: null };
          } else {
            next[key] = { action: "unresolved", packId: null };
          }
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [analysis, packOptions]);

  const handleDownloadTemplate = () => {
    window.open("/api/campaign-import-packs/template", "_blank");
  };

  const handleImportFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls") && !lower.endsWith(".csv")) {
      toast({
        title: "Unsupported file",
        description: "Use .xlsx, .xls, or .csv for the pack booking sheet.",
        variant: "destructive",
      });
      return;
    }
    setImportFile(file);
    resetImportRunState();
  };

  const runAnalysis = async () => {
    if (!importFile || !selectedRetailerId || !selectedCycleId) return;
    const requestId = analysisRequestRef.current + 1;
    analysisRequestRef.current = requestId;
    try {
      setIsAnalysing(true);
      const formData = new FormData();
      formData.append("importFile", importFile);
      formData.append("retailerId", String(selectedRetailerId));
      formData.append("cycleId", String(selectedCycleId));
      const response = await apiFetch("/api/campaign-import-packs/analyse", { method: "POST", body: formData });
      const payload = await response.json();
      if (requestId !== analysisRequestRef.current) return;
      if (!response.ok || !payload.success) {
        throw new Error(payload?.message || payload?.error || "Failed to analyse file");
      }
      setAnalysis(payload.analysis);
      setPackDecisions({});
      setCurrentStep(2);
      toast({
        title: "Import analysed",
        description: `Detected ${payload.analysis.counts.campaignsDetected} campaigns across ${payload.analysis.counts.uniquePackNames} pack names.`,
      });
    } catch (error: any) {
      if (requestId !== analysisRequestRef.current) return;
      const rawMessage = error?.message || "Could not analyse import file.";
      toast({
        title: "Analysis failed",
        description: adviceForImportAnalyseError(rawMessage) ?? rawMessage,
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      if (requestId === analysisRequestRef.current) setIsAnalysing(false);
    }
  };

  const confirmAllSuggestedPacks = () => {
    setPackDecisions((prev) => {
      const next = { ...prev };
      for (const [importName, decision] of Object.entries(next)) {
        if (decision.isSuggested && decision.packId) {
          next[importName] = { ...decision, isSuggested: false, action: "use_existing" };
        }
      }
      return next;
    });
  };

  const handleCreateCampaigns = async (retryFailedOnly = false) => {
    if (!analysis || !selectedRetailerId || !selectedCycleId) return;
    setIsCreatingCampaigns(true);
    const created: Array<{ id: number; name: string }> = createdCampaigns.slice();
    const failures: Array<{ name: string; error: string }> = [];
    const existingStatus = campaignCreationStatus;
    setCampaignCreationStatus((prev) => {
      if (retryFailedOnly && prev.length === analysis.rows.length) {
        return prev.map((entry) => (
          entry.status === "failed" ? { ...entry, status: "pending", error: undefined } : entry
        ));
      }
      return analysis.rows.map((row, index) => {
        const existing = prev[index];
        if (existing?.status === "success") return existing;
        return { name: row.campaignName, status: "pending" as const };
      });
    });

    for (let index = 0; index < analysis.rows.length; index += 1) {
      const row = analysis.rows[index];
      const existing = existingStatus[index];
      if (existing?.status === "success") continue;

      const packNames = packNamesForRow(row);
      setCampaignCreationStatus((prev) =>
        prev.map((entry, i) => (i === index ? { ...entry, status: "creating" } : entry)),
      );
      const built = buildImportedPackGroups({
        packNames,
        resolvePackId: (name) => resolvePackIdForCampaign(row.rowNumber, name),
        seeds: analysis.groupSeeds ?? [],
        createGroupId: () => crypto.randomUUID(),
      });

      if (built.error) {
        failures.push({ name: row.campaignName, error: built.error });
        setCampaignCreationStatus((prev) =>
          prev.map((entry, i) => (i === index ? { ...entry, status: "failed", error: built.error! } : entry)),
        );
        continue;
      }
      const groups = built.groups;

      const payload = {
        campaign: {
          name: row.campaignName,
          description: row.comments || `Imported from ${importFile?.name ?? "pack booking import"}`,
          cycleId: selectedCycleId,
          retailerId: selectedRetailerId,
          statusId: 1,
          campaignTypeKey: "Standard",
          createRemoval: false,
        },
        retailers: [selectedRetailerId],
        stores: [],
        packs: [],
        media: [],
        groups,
      };

      try {
        const response = await apiFetch("/api/campaigns/with-relationships", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok || !body?.id) {
          throw new Error(body?.details || body?.error || `Failed creating campaign '${row.campaignName}'`);
        }
        const newId = body.id as number;
        const newName = (body.name as string) ?? row.campaignName;
        created.push({ id: newId, name: newName });
        setCampaignCreationStatus((prev) =>
          prev.map((entry, i) => (i === index ? { ...entry, status: "success", id: newId } : entry)),
        );
      } catch (rowError: any) {
        const message = rowError?.message || `Unknown error creating '${row.campaignName}'`;
        failures.push({ name: row.campaignName, error: message });
        setCampaignCreationStatus((prev) =>
          prev.map((entry, i) => (i === index ? { ...entry, status: "failed", error: message } : entry)),
        );
      }
    }

    setCreatedCampaigns(created);
    setIsCreatingCampaigns(false);

    if (failures.length === 0 && created.length > 0) {
      toast({ title: "Campaigns created", description: `Created ${created.length} draft campaign(s) with editable pack groups.` });
      setLocation(campaignsListUrl);
      return;
    } else if (created.length === 0) {
      toast({
        title: "Creation failed",
        description: `All ${failures.length} campaign(s) failed.`,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Partial import",
        description: `Created ${created.length} of ${analysis.rows.length}. ${failures.length} failed.`,
        variant: "destructive",
      });
    }
  };

  const goNext = () => {
    if (currentStep === 4) return;
    if (!canProceedFromStep(currentStep)) {
      toast({
        title: "Step blocked",
        description: currentStepBlockReason ?? "Resolve blockers in the current step before continuing.",
        variant: "destructive",
      });
      return;
    }
    setCurrentStep((prev) => (prev + 1) as WizardStep);
  };

  const goBack = () => {
    if (currentStep === 1) return;
    setCurrentStep((prev) => (prev - 1) as WizardStep);
  };

  const renderPackStatusBadge = (status: CampaignTile["packStatus"]) => {
    if (status === "matched") return <WizardPill tone="success">Pack matched</WizardPill>;
    if (status === "suggested") return <WizardPill tone="warning">Suggested</WizardPill>;
    if (status === "ambiguous") return <WizardPill tone="danger">Ambiguous</WizardPill>;
    if (status === "unmatched") return <WizardPill tone="danger">Unmatched</WizardPill>;
    return <WizardPill tone="neutral">Pending</WizardPill>;
  };

  return (
    <div>
      <div className="bg-white shadow-sm border-b border-gray-100 p-4 lg:p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-800">Create Campaign: Import by Pack</h2>
            <p className="text-gray-600 mt-1">
              Bulk create draft pack campaigns from a campaign booking sheet (campaign name + pack name).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleDownloadTemplate}>
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
            <Link href="/campaign-upload">
              <Button variant="outline">
                <FileText className="h-4 w-4 mr-2" />
                Format Import
              </Button>
            </Link>
            <Link href="/create-campaign">
              <Button variant="ghost">
                <Target className="h-4 w-4 mr-2" />
                Manual Flow
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="p-4 lg:p-6">
        <div className="max-w-6xl mx-auto space-y-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-2 mb-3">
                {STEP_LABELS.map((step) => (
                  <WizardPill key={step.id} tone={step.id <= currentStep ? "active" : "neutral"}>
                    {step.id}. {step.label}
                  </WizardPill>
                ))}
              </div>
              <Progress value={stepProgress} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{STEP_LABELS[currentStep - 1].label}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {currentStep === 1 && (
                <>
                  <div className="rounded-md border bg-amber-50 border-amber-200 px-3 py-2 text-sm text-amber-900">
                    Choose the campaign year first. Pack names in the file must match packs configured for the selected retailer.
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Campaign year</label>
                      <div className="border rounded px-3 py-2 min-h-[42px] flex items-center">
                        <YearFilter showIcon={false} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Retailer</label>
                      <select
                        className="border rounded px-3 py-2 text-sm w-full"
                        value={selectedRetailerId ?? ""}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          if (Number.isFinite(val) && val > 0) setSelectedRetailerId(val);
                          else {
                            cycleLoadRequestRef.current += 1;
                            setSelectedRetailerId(null);
                            setCycles([]);
                            setSelectedCycleId(null);
                            setIsCyclesLoading(false);
                          }
                          resetImportRunState();
                        }}
                      >
                        <option value="">— Select retailer —</option>
                        {retailers.map((r) => (
                          <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Cycle</label>
                      <select
                        className="border rounded px-3 py-2 text-sm w-full"
                        disabled={!selectedRetailerId || isCyclesLoading}
                        value={selectedCycleId ?? ""}
                        onChange={(e) => {
                          const val = Number(e.target.value);
                          setSelectedCycleId(Number.isFinite(val) && val > 0 ? val : null);
                          resetImportRunState();
                        }}
                      >
                        <option value="">
                          {isCyclesLoading
                            ? `Loading ${selectedYear} cycles...`
                            : !selectedRetailerId
                              ? "Select a retailer first"
                              : cycles.length === 0
                                ? `No ${selectedYear} cycles found`
                                : `— Select ${selectedYear} cycle —`}
                        </option>
                        {cycles.map((c) => (
                          <option key={c.id} value={c.id}>{getCycleOptionLabel(c)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {selectedRetailerId && selectedCycleId ? (
                    <>
                      <input
                        ref={importInputRef}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        className="hidden"
                        onChange={handleImportFileSelect}
                      />
                      {!importFile ? (
                        <div
                          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary"
                          onClick={() => importInputRef.current?.click()}
                        >
                          <CloudUpload className="h-10 w-10 mx-auto mb-2 text-gray-500" />
                          <p className="font-medium">Upload Campaign Pack Booking file</p>
                          <p className="text-sm text-muted-foreground">Supports .xlsx, .xls, and .csv</p>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between border rounded-lg p-3">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5" />
                            <div>
                              <p className="font-medium">{importFile.name}</p>
                              <p className="text-xs text-muted-foreground">{(importFile.size / 1024).toFixed(1)} KB</p>
                            </div>
                          </div>
                          <Button onClick={() => void runAnalysis()} disabled={isAnalysing}>
                            {isAnalysing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            Analyse Import
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center opacity-40 select-none">
                      <CloudUpload className="h-10 w-10 mx-auto mb-2 text-gray-400" />
                      <p className="font-medium text-gray-500">Select a retailer and cycle above to upload</p>
                    </div>
                  )}
                </>
              )}

              {currentStep === 2 && analysis && (
                <>
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      Detected {analysis.counts.campaignsDetected} campaigns using {analysis.counts.uniquePackNames} unique pack names.
                    </AlertDescription>
                  </Alert>
                  {(analysis.skippedRows ?? []).length > 0 ? (
                    <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription>
                        Skipped {analysis.skippedRows.length} row(s) with a blank campaign or pack name:{" "}
                        {analysis.skippedRows
                          .map((row) => `row ${row.rowNumber} (${row.reason === "missing_pack" ? "missing pack" : "missing campaign"})`)
                          .join(", ")}
                        .
                      </AlertDescription>
                    </Alert>
                  ) : null}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {analysis.campaigns.map((campaign) => (
                      <Card key={`${campaign.rowNumber}-${campaign.campaignName}`}>
                        <CardContent className="pt-4 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium">{campaign.campaignName}</p>
                            {renderPackStatusBadge(campaign.packStatus)}
                          </div>
                          <div className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">
                              Campaign groups ({packNamesForRow(campaign).length}), one per pack
                            </p>
                            {packNamesForRow(campaign).map((packName) => (
                              <p key={packName} className="text-sm text-muted-foreground flex items-center gap-2">
                                <Package className="h-4 w-4 shrink-0" />
                                {packName}
                              </p>
                            ))}
                          </div>
                          {campaign.comments ? (
                            <p className="text-xs text-muted-foreground">Sheet notes: {campaign.comments}</p>
                          ) : null}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}

              {currentStep === 3 && analysis && (
                <>
                  {hasUnresolvedPackDecision ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Every pack name must be mapped to a retailer pack before you can continue.
                      </AlertDescription>
                    </Alert>
                  ) : hasPendingLockStatus ? (
                    <Alert>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <AlertDescription>Checking whether selected packs are locked…</AlertDescription>
                    </Alert>
                  ) : hasSuggestedPackDecisions ? (
                    <Alert className="border-amber-300 bg-amber-50 text-amber-900">
                      <AlertCircle className="h-4 w-4 text-amber-600" />
                      <AlertDescription>
                        Some packs were auto-matched by similarity. Confirm each amber mapping, or confirm all after reviewing.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>All packs confirmed. Ready to create campaigns.</AlertDescription>
                    </Alert>
                  )}

                  {hasSuggestedPackDecisions ? (
                    <Button type="button" variant="outline" size="sm" onClick={confirmAllSuggestedPacks}>
                      Confirm all suggested mappings
                    </Button>
                  ) : null}

                  <p className="text-sm text-muted-foreground">
                    Confirm the retailer pack for each campaign group. Confirmation applies only to that campaign.
                  </p>

                  <div className="space-y-4">
                    {analysis.campaigns.map((campaign) => {
                      const packNames = packNamesForRow(campaign);
                      return (
                        <Card key={`${campaign.rowNumber}-${campaign.campaignName}`}>
                          <CardContent className="pt-4 space-y-3">
                            <p className="font-medium">{campaign.campaignName}</p>
                            {packNames.map((packName) => {
                              const decisionKey = packDecisionKey(campaign.rowNumber, packName);
                              const pack = detectedPackByName.get(packName);
                              const decision = packDecisions[decisionKey] ?? {
                                action: "unresolved" as const,
                                packId: null,
                              };
                              const isSuggested = decision.isSuggested === true;
                              const isExactMatch =
                                decision.action === "matched" ||
                                (decision.action === "use_existing" && !decision.isSuggested && Boolean(decision.packId));
                              const isUnresolved =
                                decision.action === "unresolved" || (decision.action === "use_existing" && !decision.packId);
                              const selectedId = decision.packId ? String(decision.packId) : "";

                              return (
                                <div
                                  key={`${campaign.rowNumber}-${packName}`}
                                  className={`border rounded p-3 space-y-2 ${
                                    isSuggested
                                      ? "border-amber-300 bg-amber-50/40"
                                      : isUnresolved
                                        ? "border-red-300 bg-red-50/40"
                                        : "border-green-300 bg-green-50/40"
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <p className="font-medium font-mono text-sm">{packName}</p>
                                    {isSuggested ? (
                                      <WizardPill tone="warning">Suggested - verify</WizardPill>
                                    ) : isUnresolved ? (
                                      <WizardPill tone="danger">Needs action</WizardPill>
                                    ) : (
                                      <WizardPill tone="success">Confirmed</WizardPill>
                                    )}
                                  </div>
                                  <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                                    <div className="flex-1 min-w-0">
                                      <select
                                        className="border rounded px-3 py-2 text-sm w-full"
                                        value={selectedId}
                                        onChange={(e) => {
                                          const val = Number(e.target.value);
                                          if (Number.isFinite(val) && val > 0) {
                                            setPackDecisions((prev) => ({
                                              ...prev,
                                              [decisionKey]: {
                                                action: "use_existing",
                                                packId: val,
                                                isSuggested: false,
                                              },
                                            }));
                                          } else {
                                            setPackDecisions((prev) => ({
                                              ...prev,
                                              [decisionKey]: { action: "unresolved", packId: null },
                                            }));
                                          }
                                        }}
                                      >
                                        <option value="">— Select retailer pack —</option>
                                        {packOptions.map((option) => (
                                          <option key={option.id} value={option.id}>{option.name}</option>
                                        ))}
                                      </select>
                                      {isSuggested && selectedId ? (
                                        <p className="text-xs text-amber-700 mt-1">
                                          Auto-suggested by name similarity. Confirm this is correct or pick a different pack.
                                        </p>
                                      ) : null}
                                    </div>
                                    {isSuggested && selectedId ? (
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setPackDecisions((prev) => ({
                                            ...prev,
                                            [decisionKey]: {
                                              action: "use_existing",
                                              packId: Number(selectedId),
                                              isSuggested: false,
                                            },
                                          }));
                                        }}
                                      >
                                        Confirm
                                      </Button>
                                    ) : isExactMatch ? (
                                      <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-medium text-green-800">
                                        Using existing pack
                                      </div>
                                    ) : null}
                                  </div>
                                  {pack?.status === "ambiguous" && isUnresolved ? (
                                    <p className="text-xs text-red-700">
                                      Multiple packs share this name: {pack.ambiguousMatches.map((item) => item.name).join(", ")}. Pick the correct one manually.
                                    </p>
                                  ) : null}
                                  {decision.packId && packLockStatus[decision.packId] === "unlocked" ? (
                                    <p className="text-xs text-red-700">Selected pack is not locked. Lock it before creating campaigns.</p>
                                  ) : null}
                                </div>
                              );
                            })}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </>
              )}

              {currentStep === 4 && analysis && (
                <div className="space-y-4">
                  <Alert>
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertDescription>
                      {analysis.rows.length} draft pack campaign(s) will be created in the selected cycle using confirmed pack mappings.
                    </AlertDescription>
                  </Alert>

                  <div className="overflow-x-auto border rounded-md">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="text-left px-3 py-2">Campaign</th>
                          <th className="text-left px-3 py-2">Import packs</th>
                          <th className="text-left px-3 py-2">Groups</th>
                          <th className="text-left px-3 py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysis.rows.map((row, index) => {
                          const groupNames = packNamesForRow(row).map((name) => {
                            const packId = resolvePackIdForCampaign(row.rowNumber, name);
                            return packOptions.find((p) => p.id === packId)?.name ?? name;
                          });
                          const status = campaignCreationStatus[index];
                          return (
                            <tr key={`${row.rowNumber}-${row.campaignName}`} className="border-t">
                              <td className="px-3 py-2">
                                {status?.status === "success" && status.id ? (
                                  <Link href={`/campaigns/${status.id}`} className="text-primary underline">
                                    {row.campaignName}
                                  </Link>
                                ) : (
                                  row.campaignName
                                )}
                              </td>
                              <td className="px-3 py-2">{packNamesForRow(row).join(", ")}</td>
                              <td className="px-3 py-2">{groupNames.join(", ")}</td>
                              <td className="px-3 py-2">
                                {!status || status.status === "pending" ? (
                                  <WizardPill tone="neutral">Pending</WizardPill>
                                ) : status.status === "creating" ? (
                                  <WizardPill tone="info">Creating…</WizardPill>
                                ) : status.status === "success" ? (
                                  status.id ? (
                                    <Link href={`/campaigns/${status.id}`}>
                                      <WizardPill tone="success">Created #{status.id}</WizardPill>
                                    </Link>
                                  ) : (
                                    <WizardPill tone="success">Created</WizardPill>
                                  )
                                ) : (
                                  <WizardPill tone="danger">Failed</WizardPill>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {campaignCreationStatus.some((entry) => entry.status === "failed") ? (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        {campaignCreationStatus
                          .filter((entry) => entry.status === "failed")
                          .map((entry) => `${entry.name}: ${entry.error}`)
                          .join(" · ")}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {createdCampaigns.length > 0 ? (
                    <Alert>
                      <CheckCircle2 className="h-4 w-4" />
                      <AlertDescription>
                        Created {createdCampaigns.length} campaign(s):{" "}
                        {createdCampaigns.map((c) => c.name).join(", ")}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  {(() => {
                    const failedCount = campaignCreationStatus.filter((entry) => entry.status === "failed").length;
                    const successCount = campaignCreationStatus.filter((entry) => entry.status === "success").length;
                    const remaining = analysis.rows.length - successCount;
                    return (
                      <div className="flex flex-wrap gap-2">
                        {remaining > 0 ? (
                          <Button
                            onClick={() => void handleCreateCampaigns(failedCount > 0)}
                            disabled={isCreatingCampaigns}
                          >
                            {isCreatingCampaigns ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            {failedCount > 0
                              ? `Retry ${failedCount} failed campaign(s)`
                              : `Create ${analysis.rows.length} Campaign(s)`}
                          </Button>
                        ) : null}
                        {successCount > 0 ? (
                          <Link href={campaignsListUrl}>
                            <Button variant={remaining > 0 ? "outline" : "default"}>
                              View campaigns for this retailer/cycle
                            </Button>
                          </Link>
                        ) : null}
                      </div>
                    );
                  })()}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex items-center justify-between">
            <Button variant="outline" onClick={goBack} disabled={currentStep === 1}>
              Back
            </Button>
            {currentStep < 4 ? (
              <Button onClick={goNext} disabled={!canProceedFromStep(currentStep)}>
                Next Step
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
